import { useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import Slider from "react-slick";
import CustomImage from "../../atoms/customImage";
import { appendDots } from "../carouselHelpers";
import { PRODUCT_CARD_OPTIONS } from "../../../constants/common/splideOptions";
import VideoPlayer from "../../atoms/videoPlayer";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setCurrentVideoId, setLoadedVideoIds } from "../../../store/slices/plpSlice";
import { formatClassNames } from "../../../utils/common";
import { PLP_PRODUCT_VIDEO_POSITION } from "../../../constants/pageConstants/plpConstants";

/**
 * A slider component that displays a list of product images.
 * It supports autoplay functionality, which can be controlled by the parent component.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {string[]} props.productImgList - An array of image URLs to display in the slider.
 * @param {number} props.imgWidth - The width of each image in the slider.
 * @param {number} props.imgHeight - The height of each image in the slider.
 * @param {string} props.alt - The alt text for each image in the slider.
 * @param {string} props.defaultImg - The default image URL to display when `productImgList` is empty.
 * @param {boolean} props.isPriority - Whether the images should be prioritized for loading.
 * @param {boolean} props.isMounted - check client or server rendering.
 * @param {React.Ref} ref - A ref object that allows the parent component to control the slider (e.g., start or stop autoplay).
 *
 * @returns {JSX.Element} The rendered slider component.
 */
const ProductCardImageSlider = forwardRef(
  (
    {
      productImgList = [],
      imgWidth,
      imgHeight,
      alt,
      defaultImg,
      isPriority = false,
      isMounted,
      productId,
      isPLP = false,
      isSearch = false,
      videoUrl = "",
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const { loadedVideoIds, currentVideoId } = useSelector(
      (state) => ({
        loadedVideoIds: state?.plp?.loadedVideoIds,
        currentVideoId: state?.plp.currentVideoId,
      }),
      shallowEqual,
    );

    const sliderRef = useRef(null);
    const currentVideoIdRef = useRef(currentVideoId);
    const loadedVideoIdsRef = useRef(loadedVideoIds);

    useEffect(() => {
      if (currentVideoId) {
        currentVideoIdRef.current = currentVideoId;
      }
    }, [currentVideoId]);

    useEffect(() => {
      if (loadedVideoIds) {
        loadedVideoIdsRef.current = loadedVideoIds;
      }
    }, [loadedVideoIds]);
    /**
     * Pauses the slider and resets it to the first slide after a short delay.
     * @function
     */
    const pauseAndResetSlide = () => {
      if (sliderRef.current) {
        sliderRef.current.slickPause();
        setTimeout(() => {
          if (sliderRef.current) {
            sliderRef.current.slickGoTo(0, true);
          }
        }, 400);
      }
    };

    useImperativeHandle(ref, () => ({
      /**
       * Starts the autoplay for the image slider.
       * @function
       */
      startAutoplay() {
        if (sliderRef.current) {
          sliderRef.current.slickPlay();
        }
      },
      /**
       * Stops the autoplay for the image slider and resets it to the first slide.
       * @function
       */
      stopAutoplay() {
        pauseAndResetSlide();
      },
    }));

    const settings = {
      ...PRODUCT_CARD_OPTIONS,
      dots: productImgList.length > 1,
      autoplay: false,
      appendDots: (dots) => appendDots(dots, "", "", "", "", "productCardDots"),
    };
    /**
     * Handles mouse leave event on the video player. Pauses the video.
     * @function
     * @param {string} videoId - The DOM id of the video element to pause.
     */
    const onHandleMouseLeave = (videoId) => {
      const video = document.getElementById(videoId);
      if (video) {
        video.pause();
      }
    };
    /**
     * Handles the end of the video playback. Pauses the video and resumes slider autoplay.
     * @function
     * @param {string} videoId - The DOM id of the video element that ended.
     */
    const onHandleVideoEnd = (videoId) => {
      const video = document.getElementById(videoId);
      if (video) {
        video.pause();
        if (sliderRef.current) {
          sliderRef.current.slickPlay();
        }
      }
    };
    /**
     * Checks if the entire video is buffered/loaded.
     * If fully loaded, dispatches an action to mark the video as loaded.
     *
     * @param {HTMLVideoElement} video - The video element to check.
     * @param {string} videoId - The unique ID of the video.
     * @returns {boolean} - True if video is fully loaded, else false.
     */
    const checkIfFullyLoaded = (video, videoId) => {
      if (video.buffered.length > 0) {
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const duration = video.duration;
        if (duration > 0 && bufferedEnd >= duration) {
          dispatch(setLoadedVideoIds({ [videoId]: true }));
          return true;
        }
      }
      return false;
    };
    /**
     * Checks if the video is fully loaded on 'progress' event,
     * and removes the event listener if it is.
     * @param {HTMLVideoElement} video - The video element.
     * @param {string} videoId - ID of the video.
     */
    const onProgress = (video, videoId) => {
      if (checkIfFullyLoaded(video, videoId)) {
        video.removeEventListener("progress", onProgress);
      }
    };
    /**
     * Handles mouse over event on the video player. Loads and plays the video, manages previous video state, and dispatches Redux actions.
     * @function
     * @param {string} videoId - The DOM id of the video element to play.
     */
    const onHandleMouseOver = (videoId) => {
      if (sliderRef.current) {
        sliderRef.current.slickPause();
      }
      // Pause and abort previous video if any
      if (currentVideoIdRef.current !== videoId) {
        const prevVideo = document.getElementById(currentVideoIdRef.current);
        if (prevVideo) {
          prevVideo.pause();
          if (!loadedVideoIdsRef.current[currentVideoIdRef.current]) {
            prevVideo.removeAttribute("src");
            prevVideo.load(); // Clears the video and shows the poster image after removing the source
          }
        }
        dispatch(setCurrentVideoId(videoId));
      }

      const video = document.getElementById(videoId);
      if (video) {
        if (!loadedVideoIdsRef.current[videoId]) {
          if (!video.getAttribute("src")) {
            video.setAttribute("src", video.dataset.src);
            video.load();
          }
        }
        // If not loaded yet, listen for progress events until loaded
        if (!checkIfFullyLoaded(video, videoId)) {
          video.addEventListener("progress", () => onProgress(video, videoId));
        }
        video.play();
      }
    };

    /**
     * Handles video playback errors. Resumes slider autoplay.
     * @function
     */
    const onVideoPlaybackError = () => {
      if (sliderRef.current) {
        sliderRef.current.slickPlay();
      }
    };

    return (
      <>
        {!isMounted && productImgList.length >= 0 ? (
          <CustomImage
            src={defaultImg}
            width={imgWidth}
            height={imgHeight}
            alt={alt}
            isPriority={isPriority}
            customStyle={{ width: "100%", height: "100%" }}
            itemProp="image"
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        ) : (
          <>
            <Slider
              {...settings}
              ref={sliderRef}
              className="respective w-full"
              dotsClass={formatClassNames("slick-dots", (isPLP || isSearch) && videoUrl && "plpPlayDots")}
            >
              {productImgList.map((item, index) => {
                return (
                  <>
                    {(isPLP || isSearch) && videoUrl && index === PLP_PRODUCT_VIDEO_POSITION ? (
                      <VideoPlayer
                        key={item}
                        videoUrl={videoUrl}
                        preload="none"
                        onMouseOver={() => onHandleMouseOver(`${productId}-${index}`)}
                        onMouseLeave={() => onHandleMouseLeave(`${productId}-${index}`)}
                        onEnded={() => onHandleVideoEnd(`${productId}-${index}`)}
                        aria-label="Video player"
                        id={`${productId}-${index}`}
                        isMuted={true}
                        poster={productImgList[index + 1]}
                        onError={onVideoPlaybackError}
                        width={imgWidth}
                        height={imgHeight}
                      />
                    ) : (
                      <CustomImage
                        key={index}
                        src={item}
                        width={imgWidth}
                        height={imgHeight}
                        alt={alt}
                        isPriority={isPriority && index === 0}
                        customStyle={{ width: "100%", height: "100%" }}
                        itemProp="image"
                        sizes="(max-width: 768px) 100vw, 50vw"
                      />
                    )}
                  </>
                );
              })}
            </Slider>
          </>
        )}
      </>
    );
  },
);

ProductCardImageSlider.displayName = "ProductCardImageSlider";

ProductCardImageSlider.propTypes = {
  productImgList: PropTypes.arrayOf(PropTypes.string),
  imgWidth: PropTypes.number.isRequired,
  imgHeight: PropTypes.number.isRequired,
  alt: PropTypes.string.isRequired,
  defaultImg: PropTypes.string,
  isPriority: PropTypes.bool,
  isMounted: PropTypes.bool,
  isPLP: PropTypes.bool,
  isSearch: PropTypes.bool,
  productId: PropTypes.string,
  videoUrl: PropTypes.string,
};

export default ProductCardImageSlider;
